import { getValidAmountInMinorCurrency } from '@/views/config/coupon/helper/utils';
import { getPreviewEligibility } from '@/apis';
import { logger } from '@/utils/log';
import { isInrCurrency } from '@/utils/currency';
import { getUpdateForm } from '@/flows/EDIT';

export const PRORATED_OF = {
  CURRENT: 'current',
  UPDATED: 'updated',
}

export const PREVIEW_ACTION_TYPES = {
  resetProratedCharges: 'reset/prorated/charges',
  setProratedCharges: 'set/prorated/charges',
  checkPreviewEligibility : 'check/preview/eligibility ',
};

export const PREVIEW_API_ACTIONS_ENUM = {
  PRORATED: "prorated",
  NEXT_CHARGE_DATE: "nextChargeDate"
}

const state = {
  isProratedChargeAmountCalculated: false, // defines if user can continue the preview flow
  proratedChargeAmount: 0,
  proratedChargeTaxAmount: 0,
  proratedChargeTotalAmount: 0,
  proratedChargeDetails: [],
  proratedChargeOtherDetails: [],
  refundCredit: 0,
  creditBalance: 0,
  pendingPayment: 0,
  totalReferralCredit: 0,
  isReplaceSubscription: false,
  isLoadingPreview: false,  // determines if "preview" parameter should be added to the submit request
  isPreviewEligible: false, // result of /opc/preview/support_check API call
  previewType: '',
  isProrated: false,
  isProratedLoading: false,
  nextChargeDate: {
    nextMonthlyDate: null,
    nextAnnualDate: null,
    nextQuarterlyDate: null,
    nextTwoYearsDate: null,
    nextThreeYearsDate: null,
    pendingPlanList:[]
  }
}

const getters = {
  isInrCitEligible: (state, getters) => { // current flow is CIT
    const { inrCitEnabled, isPreviewEligible } = getters;
    return inrCitEnabled && isPreviewEligible;
  },
  isProratedChargeAmountCalculated: (state) => state.isProratedChargeAmountCalculated,
  isLoadingPreview: (state) => state.isLoadingPreview,
  isPreviewEligible: (state) => state.isPreviewEligible,
  getProratedChargeDetails: ({proratedChargeDetails}) => {
    return proratedChargeDetails;
  },
  getProratedChargeOtherDetails: ({proratedChargeOtherDetails}) => {
    return proratedChargeOtherDetails;
  },
  getProratedChargeSubTotal: ({proratedChargeDetails}) => {
    return proratedChargeDetails.filter(x => x.of === PRORATED_OF.UPDATED).reduce((acc, x) => {
      return acc + x.chargeAmount + x.tax;
    }, 0);
  },
  getRefundCredit: ({refundCredit}) => {
    return refundCredit;
  },
  getIsReplaceSubscription: ({isReplaceSubscription}) => {
    return isReplaceSubscription;
  },
  getProratedChargeAmount: (state) => state.proratedChargeAmount,
  getProratedChargeTaxAmount: (state) => state.proratedChargeTaxAmount,
  getProratedChargeTotalAmount: (state) => ( state.proratedChargeTotalAmount - state.creditBalance + state.pendingPayment),
  getTodaysTotal: (state) => {
    if(!state.isReplaceSubscription) return state.proratedChargeTotalAmount - state.creditBalance + state.pendingPayment;
    return state.proratedChargeTotalAmount;
  },
  getCreditBalance: (state) => state.creditBalance - state.pendingPayment,
  getTotalReferralCredit: (state) => state.totalReferralCredit,
  getPreviewType: (state) => state.previewType,
  isProrated: (state) => {
    return state.isProrated;
  },
  isProratedLoading: (state) => {
    return state.isProratedLoading;
  },
  nextChargeDate: (state) => state.nextChargeDate,
  inrPreviewApiCheck4Coupon: (state, getters) => {
    return isInrCurrency() && getters.useSpecialSupportCoupon;
  }
}

const mutations = {
  setProratedChargeDetails(state, data = []) {
    state.proratedChargeDetails = data;
  },
  setProratedChargeOtherDetails(state, data = []) {
    state.proratedChargeOtherDetails = data;
  },
  setRefundCredit(state, data = 0){
    state.refundCredit = data;
  },
  setIsReplaceSubscription(state, data = false){
    state.isReplaceSubscription = data;
  },
  setProratedChargeAmount(state, data = 0) {
    state.proratedChargeAmount = data;
  },
  setProratedChargeTaxAmount(state, data = 0) {
    state.proratedChargeTaxAmount = data;
  },
  setProratedChargeTotalAmount(state, data = 0) {
    state.proratedChargeTotalAmount = data;
  },
  setCreditBalance(state, data = 0){
    state.creditBalance = data;
  },
  setTotalReferralCredit(state, data = 0){
    state.totalReferralCredit = data;
  },
  setPendingPayment(state, data = 0){
    state.pendingPayment = data;
  },
  setIsProratedChargeAmountCalculated(state, data = {}) {
    state.isProratedChargeAmountCalculated = data;
  },
  setIsLoadingPreview(state, data) {
    state.isLoadingPreview = data;
  },
  setIsPreviewEligible(state, data) {
    state.isPreviewEligible = data;
  },
  setPreviewType(state, data){
    state.previewType = data;
  },
  setIsProrated(state, data = true) {
    state.isProrated = data;
  },
  setIsProratedLoading(state, data = true) {
    state.isProratedLoading = data;
  },
  setNextChargeDate(state, data) {
    // clear
    state.nextChargeDate = {
      isVaryByItem: false,
      nextMonthlyDate: null,
      nextAnnualDate: null,
      nextQuarterlyDate: null,
      nextTwoYearsDate: null,
      nextThreeYearsDate: null,
      pendingPlanList:[]
    }
    Object.keys(data).forEach(k=>{
      state.nextChargeDate[k] = data[k]
    })
  }
}

const actions = {
  [PREVIEW_ACTION_TYPES.resetProratedCharges]({ commit }) {
    commit('setProratedChargeAmount', 0);
    commit('setProratedChargeTaxAmount', 0);
    commit('setProratedChargeTotalAmount', 0);
    commit('setProratedChargeDetails', []);
    commit('setProratedChargeOtherDetails', []);
    commit('setIsReplaceSubscription', false);
    commit('setRefundCredit', 0);
    commit('setCreditBalance', 0);
    commit('setPendingPayment', 0);
    commit('setTotalReferralCredit', 0);
    commit('setIsProratedChargeAmountCalculated', false);
  },
  [PREVIEW_ACTION_TYPES.setProratedCharges]({ commit }, payload = {}) {
    const retainAmount = payload.__retain??false;
    commit('setProratedChargeDetails', payload.details??[]);
    commit('setProratedChargeOtherDetails', payload.otherDetails??[]);
    commit('setIsReplaceSubscription', payload.replaceSubscription??false);
    commit('setRefundCredit', retainAmount ? payload.refundCredit : getValidAmountInMinorCurrency(payload.refundCredit));
    commit('setProratedChargeAmount', retainAmount ? payload.amount : getValidAmountInMinorCurrency(payload.amount));
    commit('setProratedChargeTaxAmount', retainAmount ? payload.taxAmount : getValidAmountInMinorCurrency(payload.taxAmount));
    commit('setProratedChargeTotalAmount', retainAmount ? payload.totalAmount : getValidAmountInMinorCurrency(payload.totalAmount));
    commit('setCreditBalance', retainAmount ? payload.creditBalance : getValidAmountInMinorCurrency(payload.creditBalance || 0 ));
    commit('setPendingPayment', retainAmount ? payload.pendingPayment : getValidAmountInMinorCurrency(payload.pendingPayment || 0 ));
    commit('setTotalReferralCredit', retainAmount ? payload.totalReferralCredit : getValidAmountInMinorCurrency(payload.totalReferralCredit || 0 ));
    commit('setIsProratedChargeAmountCalculated', true); // enable "Proceed to Payment" button
  },
  /**
   * This action is called on page load to define if the current flow is eligible for CIT
   */
  async [PREVIEW_ACTION_TYPES.checkPreviewEligibility]({ getters, commit },  payload = {}) {
    const { inrCitEnabled, getSelectedPlans, orderDataPayload } = getters;
    if (!inrCitEnabled) {
      return;
    }

    try {
      const data = payload.flowType === 'add' ?
        getPreviewEligibilityDataForAdd(payload.flowType, getSelectedPlans, orderDataPayload.coupon) :
        getPreviewEligibilityDataForEdit(getters, payload.flowType, getSelectedPlans, orderDataPayload.coupon);
      let result = false;
      // exclude for coupon cases
      if (getters.inrPreviewApiCheck4Coupon) {
        result = false;
      } else {
        const resultData = await getPreviewEligibility(data);
        result = resultData?.result;
      }
      commit('setIsPreviewEligible', result);
      commit('setOnSessionPaymentEnabled', result); // switch to MIT in case of 'false'
    } catch (e) {
      logger.error('get preview eligibility API error', e);
      commit('setIsPreviewEligible', false);
      commit('setOnSessionPaymentEnabled', false); // switch to MIT in case of error
    }
  },
}

const getPreviewEligibilityDataForAdd = (flowType, selectedPlans, coupon) => {
  return {
    flowType: flowType,
    planList: selectedPlans.map(plan => {
      return {
        planCode: plan.plan_code,
        planType: plan.plan_type,
        quantity: plan.cartInfo.selected_number,
      };
    }),
    couponData: {
      code: coupon.code,
      fromCampaignLink: coupon.fromCampaignLink,
      cancelCampaign: coupon.cancelCampaign,
    },
  };
};

const getPreviewEligibilityDataForEdit = (getters, flowType, selectedPlans, coupon) => {
  const { partnerRefer } = getters;
  const updateForm = getUpdateForm(getters);
  return {
    flowType: flowType,
    planList: selectedPlans.map(plan => {
      return {
        oldPlanCode: plan.old_plan_code,
        planCode: plan.plan_code,
        planType: plan.plan_type,
        quantity: plan.cartInfo.selected_number,
      };
    }),
    couponData: {
      code: coupon?.code,
      fromCampaignLink: coupon?.fromCampaignLink,
      cancelCampaign: coupon?.cancelCampaign,
    },
    actionType: updateForm.actionType,
    multiRatePlan: updateForm.multiRatePlan || false,
    extensionData: {
      jointsaleCode: partnerRefer?.jointsaleCode,
      referralCode: partnerRefer?.referralCode,
    },
  };
};

export default {
  state,
  getters,
  mutations,
  actions,
}
